/**
 * Cookie consent — KVKK + GDPR compliance helper.
 *
 * Why a separate module: any place that loads analytics, ads, or third-party
 * tracking should call `hasMarketingConsent()` before firing. Centralizing the
 * key + read/write here avoids the bug where one component remembers consent
 * but another forgets to check it.
 *
 * Storage shape (kept simple, future-extensible):
 *   {
 *     v: 1,
 *     status: "accepted" | "rejected",
 *     timestamp: number    // ms epoch when the user decided
 *   }
 *
 * Backwards compatibility: the previous version wrote the literal string
 * "accepted" — we still read that as { status: "accepted" }.
 */

export const COOKIE_CONSENT_KEY = "humo_cookie_consent";
export const COOKIE_CONSENT_VERSION = 1;

export type ConsentStatus = "accepted" | "rejected" | "unknown";

interface StoredConsent {
  v: number;
  status: "accepted" | "rejected";
  timestamp: number;
}

function safeRead(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    // Old format: just the literal string
    if (raw === "accepted" || raw === "rejected") {
      return { v: 0, status: raw as "accepted" | "rejected", timestamp: 0 };
    }
    const parsed = JSON.parse(raw) as Partial<StoredConsent>;
    if (parsed && (parsed.status === "accepted" || parsed.status === "rejected")) {
      return {
        v: parsed.v ?? COOKIE_CONSENT_VERSION,
        status: parsed.status,
        timestamp: parsed.timestamp ?? 0,
      };
    }
    return null;
  } catch {
    return null;
  }
}

function safeWrite(status: "accepted" | "rejected"): void {
  try {
    const value: StoredConsent = {
      v: COOKIE_CONSENT_VERSION,
      status,
      timestamp: Date.now(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(value));
  } catch {
    // localStorage unavailable (incognito, quota, etc.) — silently ignore.
    // The banner will re-prompt next visit, which is acceptable.
  }
}

export function getConsentStatus(): ConsentStatus {
  return safeRead()?.status ?? "unknown";
}

/**
 * True only if the user has actively accepted. "unknown" returns false so we
 * fail-closed (no tracking until explicit opt-in).
 */
export function hasMarketingConsent(): boolean {
  return getConsentStatus() === "accepted";
}

export function setAccepted(): void {
  safeWrite("accepted");
}

export function setRejected(): void {
  safeWrite("rejected");
}

/** Wipe consent (used by "manage cookies" UI in the future). */
export function clearConsent(): void {
  try { localStorage.removeItem(COOKIE_CONSENT_KEY); } catch { /* ignore */ }
}
